import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as opsApi from "@/features/ops/api";
import type { CsedAuditRow } from "@/features/ops/types";

export default function CsedAuditPage() {
  const [entity, setEntity] = useState("");
  const [rows, setRows] = useState<CsedAuditRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  const [exporting, setExporting] = useState(false);

  const runAudit = async () => {
    if (!entity.trim()) {
      toast.error("Enter a firm id");
      return;
    }
    setLoading(true);
    try {
      const res = await opsApi.getCsedAudit(entity.trim());
      setRows(res.rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await opsApi.downloadCsedAudit(entity.trim());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>CSED discrepancy audit</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="entity">Firm id</Label>
            <Input
              id="entity"
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              placeholder="firm UUID"
              className="w-80"
            />
          </div>
          <Button onClick={runAudit} disabled={loading}>
            {loading ? "Auditing…" : "Run audit"}
          </Button>
          {rows !== null && (
            <Button variant="outline" onClick={exportXlsx} disabled={!entity.trim() || exporting}>
              {exporting ? "Exporting…" : "Export .xlsx"}
            </Button>
          )}
        </CardContent>
      </Card>

      {rows !== null && (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Form</TableHead>
                <TableHead>Stored CSED</TableHead>
                <TableHead>Recomputed CSED</TableHead>
                <TableHead>Practitioner</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length ? (
                rows.map((r, i) => (
                  <TableRow key={`${r.client}-${r.taxYear}-${i}`}>
                    <TableCell>{r.client}</TableCell>
                    <TableCell>{r.taxYear}</TableCell>
                    <TableCell>{r.form}</TableCell>
                    <TableCell>{r.calculatedCsed ?? "—"}</TableCell>
                    <TableCell>{r.irsCsed ?? "—"}</TableCell>
                    <TableCell>{r.primaryPractitioner}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                    No CSED discrepancies for this firm.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
